import { useMemo } from 'react'
import { ArrowRight } from 'lucide-react'
import { findFirstReadyAction, type StepReadiness } from '@/features/projects/step-readiness'
import type { StepNode } from '@/api/types'

interface NextUpStripProps {
  snapshotSteps: StepNode[]
  completedStepIds: Set<string>
  readinessMap: Map<string, StepReadiness>
}

/**
 * Phase 13 — doing-view header pointer to the first leaf Action that's both incomplete and not
 * blocked by any unmet dependency. Hides itself when there's nothing ready (everything's blocked
 * or everything's done — the outline's "you're done!" empty state covers the latter). Click
 * scrolls the outline to the target Step and briefly highlights it.
 */
export const NextUpStrip = ({
  snapshotSteps,
  completedStepIds,
  readinessMap,
}: NextUpStripProps) => {
  const next = useMemo(
    () => findFirstReadyAction(snapshotSteps, completedStepIds, readinessMap),
    [snapshotSteps, completedStepIds, readinessMap]
  )
  if (!next) return null
  return (
    <div className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">
      <span className="font-medium">Next Up:</span>{' '}
      <a
        href={`#step-${next.id}`}
        className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
        onClick={(e) => {
          const target = document.getElementById(`step-${next.id}`)
          if (target) {
            e.preventDefault()
            target.scrollIntoView({ behavior: 'smooth', block: 'center' })
            target.classList.add('ring-2', 'ring-primary')
            window.setTimeout(() => target.classList.remove('ring-2', 'ring-primary'), 1500)
          }
        }}
      >
        {next.title}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </a>
    </div>
  )
}
